import React from 'react';
import {
  Accordion,
  Button,
  Checkbox,
  Flex,
  Heading,
  Table,
  Tag,
  Text,
  TableColumnSort,
  Select,
  TextInput,
  useToasts,
} from '@applyboard/crystal-ui';
import { useQueryClient } from '@tanstack/react-query';

import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { StyledTable } from '~/components/uie-fix/StyledTable';
import { GradePoint, PostSecondaryCourse, TranscriptDocument } from '~/types/transcriptExtraction';
import { resolveAttributeValue, formatTranscriptDate } from '~/utils/transcriptExtraction';
import { ChevronRightOutlineIcon, CheckMarkOutlineIcon, CloseOutlineIcon } from '~/icons';
import { ArrowRightOutlineIcon, ChevronLeftOutlineIcon, EditOutlineIcon } from '@applyboard/ui-icons';
import { EducationHistory, Application } from '~/data/useApplication';
import { useCourses } from '~/data/useCourses';
import { useUpdateCourseMapping, useCreateCourseMapping } from '~/data/useCourseMappings';
import { useUpdateEducationHistoryCourses } from '~/data/useEducationHistoryCourses';
import { useRecalculateGpas } from '../../../data/useRecalculateGpas';
import { DeleteCoursesDialog } from './DeleteCoursesDialog';
import type {
  PatchCourseMappingResponseBody,
  PostCourseMappingResponseBody,
} from '@ab/ams-school-api/types/private/v1';
import { uuidv4 } from '~/utils/uid';
import { AddCourseDialog, AddCourseErrors, AddCourseState } from './../AddCourseDialog';

type CourseRow = {
  courseName?: string;
  courseCode?: string;
  marksObtained?: string;
  grade?: string;
  credits?: string;
  semester?: string;
  arrow?: 'mapped';
  mappedCourseName?: string;
  mappedGrade?: string;
  courseMappingId?: string;
  educationCountryCode?: string;
  educationLevel?: string;
  institutionName?: string;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';
  manuallyEdited?: boolean;
  aiGenerated?: boolean;
  // For editing - unique course identifier from backend
  id: string;
  transcriptDocumentId?: string;
};

type EditingCourseState = {
  courseName: string;
  courseCode: string;
  marksObtained: string;
  grade: string;
  credits: string;
  semester: string;
};

type ValidationErrors = Partial<Record<keyof EditingCourseState, string>>;

type ConfidenceFilter = 'ALL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';

// Course update payload with id for matching
export type CourseValue = {
  courseName?: string;
  courseCode?: string;
  marksObtained?: string;
  grade?: string;
  credits?: string;
  semester?: string;
  results?: string;
  minimum?: string;
  maximum?: string;
};

export type CourseEditPayload = {
  id: string;
  schoolProvidedValue?: CourseValue;
  manuallyEdited?: boolean;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';
};

type CourseMappingResult = PatchCourseMappingResponseBody['data'] | PostCourseMappingResponseBody['data'] | null;

type TableData = {
  key: string;
  title: string;
  rows: CourseRow[];
  extractedOn?: string;
  updatedAt: string;
  transcriptDocumentId?: string;
  allCourses: Array<CourseEditPayload>;
};

type EditableTextCellProps = {
  value: string;
  valueRef: React.MutableRefObject<string>;
  isEditing: boolean;
  displayValue: string;
  ariaLabel: string;
  disabled?: boolean;
  invalid?: boolean;
};

// Validation function for course fields
const validateCourse = (values: EditingCourseState): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!values.courseName?.trim()) {
    errors.courseName = 'Course name is required';
  }

  if (values.courseCode && !values.courseCode.trim()) {
    errors.courseCode = 'Course code cannot be empty whitespace';
  }

  if (values.marksObtained?.trim() && isNaN(Number(values.marksObtained))) {
    errors.marksObtained = 'Marks must be a valid number';
  }

  if (values.grade && !values.grade.trim()) {
    errors.grade = 'Grade cannot be empty whitespace';
  }

  if (values.credits?.trim() && isNaN(Number(values.credits))) {
    errors.credits = 'Credits must be a valid number';
  }

  if (values.semester && !values.semester.trim()) {
    errors.semester = 'Semester cannot be empty whitespace';
  }

  return errors;
};

const validateNewCourse = (values: AddCourseState): AddCourseErrors => {
  const errors: AddCourseErrors = {};

  if (!values.courseName.trim()) {
    errors.courseName = 'Course name is required';
  }

  if (values.courseCode && !values.courseCode.trim()) {
    errors.courseCode = 'Course code cannot be empty whitespace';
  }

  if (values.marksObtained.trim() && isNaN(Number(values.marksObtained))) {
    errors.marksObtained = 'Marks must be a valid number';
  }

  if (values.grade && !values.grade.trim()) {
    errors.grade = 'Grade cannot be empty whitespace';
  }

  if (values.credits.trim() && isNaN(Number(values.credits))) {
    errors.credits = 'Credits must be a valid number';
  }

  if (values.semester && !values.semester.trim()) {
    errors.semester = 'Semester cannot be empty whitespace';
  }
  return errors;
};

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  valueRef,
  isEditing,
  displayValue,
  ariaLabel,
  disabled = false,
  invalid = false,
}) => {
  const [localValue, setLocalValue] = React.useState(value);

  React.useEffect(() => {
    if (isEditing) {
      setLocalValue(value);
    }
  }, [isEditing, value]);

  if (!isEditing) {
    return <Table.TextCell>{displayValue}</Table.TextCell>;
  }

  return (
    <Table.Cell>
      <TextInput
        value={localValue}
        onChange={(newValue) => {
          setLocalValue(newValue);
          valueRef.current = newValue;
        }}
        aria-label={ariaLabel}
        disabled={disabled}
        size="sm"
        invalid={invalid}
      />
    </Table.Cell>
  );
};

const getLatestTranscript = (docs: TranscriptDocument[]): TranscriptDocument | undefined => {
  return docs.slice().sort((a, b) => {
    const getTime = (doc: TranscriptDocument) => {
      const { extractedOn, updatedAt, createdAt } = doc.attributes;
      return new Date(extractedOn ?? updatedAt ?? createdAt ?? 0).getTime();
    };
    return getTime(b) - getTime(a);
  })[0];
};

type CoursesAndGradesSectionProps = {
  educationHistoryItems?: EducationHistory[];
};

export function CoursesAndGradesSection(props?: CoursesAndGradesSectionProps) {
  const {
    application: { educationHistories, id: applicationId, studentPersonaInformation },
  } = useAppProcessing();

  const queryClient = useQueryClient();
  const toast = useToasts();
  const studentId = studentPersonaInformation?.id;

  const educationHistoryItems = React.useMemo(
    () => props?.educationHistoryItems ?? educationHistories.educationHistoryItems ?? [],
    [props?.educationHistoryItems, educationHistories.educationHistoryItems]
  );

  const [confidenceFilterByKey, setConfidenceFilterByKey] = React.useState<Record<string, ConfidenceFilter>>({});
  const defaultSort: TableColumnSort = React.useMemo(() => [{ id: 'semester', desc: true }], []);
  const pageSize = 10;

  const [sortStateByKey, setSortStateByKey] = React.useState<Record<string, TableColumnSort>>({});
  const [currentPageByKey, setCurrentPageByKey] = React.useState<Record<string, number>>({});
  const [expandedByKey, setExpandedByKey] = React.useState<Record<string, boolean>>({});

  const [editingRowKey, setEditingRowKey] = React.useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = React.useState<string>('');
  const [hasDropdownChanged, setHasDropdownChanged] = React.useState<boolean>(false);
  const [editedValues, setEditedValues] = React.useState<EditingCourseState | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({});
  const initialAddCourseState = React.useMemo<AddCourseState>(
    () => ({
      courseCode: '',
      courseName: '',
      marksObtained: '',
      grade: '',
      credits: '',
      semester: '',
    }),
    []
  );
  const [isAddCourseOpen, setIsAddCourseOpen] = React.useState(false);
  const [addCourseValues, setAddCourseValues] = React.useState<AddCourseState>(initialAddCourseState);
  const [addCourseErrors, setAddCourseErrors] = React.useState<AddCourseErrors>({});
  const [addCourseTable, setAddCourseTable] = React.useState<TableData | null>(null);
  const [isWaitingForRefetch, setIsWaitingForRefetch] = React.useState(false);
  // Temporary local state for newly added courses, cleared when query updates
  const [pendingAddedCoursesByKey, setPendingAddedCoursesByKey] = React.useState<Record<string, CourseRow[]>>({});

  // Selection state for course deletion
  const [selectedCoursesByKey, setSelectedCoursesByKey] = React.useState<Record<string, CourseRow[]>>({});

  const courseNameRef = React.useRef<string>('');
  const courseCodeRef = React.useRef<string>('');
  const marksObtainedRef = React.useRef<string>('');
  const gradeRef = React.useRef<string>('');
  const creditsRef = React.useRef<string>('');
  const semesterRef = React.useRef<string>('');

  const { courses, isLoadingCourses } = useCourses({
    'filter[isArchived]': false,
  });

  const { updateCourseMapping, isUpdatingCourseMapping } = useUpdateCourseMapping(applicationId);
  const { createCourseMapping, isCreatingCourseMapping } = useCreateCourseMapping(applicationId);
  const { updateEducationHistoryCourses, isUpdatingEducationHistoryCourses } = useUpdateEducationHistoryCourses();
  const { recalculateGpa } = useRecalculateGpas(applicationId);
  const isSaving =
    isUpdatingCourseMapping || isCreatingCourseMapping || isUpdatingEducationHistoryCourses || isWaitingForRefetch;

  const triggerGpaRecalculation = React.useCallback(
    async (educationHistoryKey: string) => {
      const eh = educationHistoryItems.find((item) => item.key === educationHistoryKey);
      const transcripts = (eh?.transcriptExtractions ?? []) as TranscriptDocument[];
      const latestTranscript = getLatestTranscript(transcripts) ?? transcripts[0];
      const allGradePoints = latestTranscript?.attributes?.gradeConversion?.gradePoints;
      const gradePoints = allGradePoints?.filter((gp) => gp.convertedGradePoint);
      if (!gradePoints || gradePoints.length === 0) return;
      try {
        await recalculateGpa({
          educationHistoryKey,
          gradePoints,
          educationLevelCode: eh?.level?.code,
        });
        queryClient.invalidateQueries(['application', applicationId]);
      } catch {
        // GPA recalculation failure is non-blocking for the course operation
      }
    },
    [educationHistoryItems, recalculateGpa, queryClient, applicationId]
  );

  const educationTables: TableData[] = React.useMemo(() => {
    return educationHistoryItems.map((item) => {
      const transcripts = (item.transcriptExtractions ?? []) as TranscriptDocument[];
      const latestTranscript = getLatestTranscript(transcripts) ?? transcripts[0];

      // Map over courses array and resolve each course's value
      // This creates a flattened structure with course fields + metadata
      type FlattenedCourse = {
        id: string;
        courseName?: string;
        courseCode?: string;
        semester?: string;
        credits?: string;
        grade?: string;
        marksObtained?: string;
        results?: string;
        minimum?: string;
        maximum?: string;
        confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED' | 'MANUAL';
        manuallyEdited?: boolean;
      };
      const coursesData: FlattenedCourse[] =
        latestTranscript?.attributes.courses
          ?.filter((course): course is PostSecondaryCourse & { id: string } => !!course.id)
          .map((course) => {
            const courseValue = resolveAttributeValue(course);
            return {
              ...courseValue,
              id: course.id,
              confidence: course.confidence,
              manuallyEdited: course.manuallyEdited,
            };
          }) ?? [];

      const allCourses: CourseEditPayload[] =
        latestTranscript?.attributes.courses
          ?.filter((course): course is PostSecondaryCourse & { id: string } => !!course.id)
          .map((course) => ({
            id: course.id,
            schoolProvidedValue: course.schoolProvidedValue ?? undefined,
            manuallyEdited: course.manuallyEdited,
          })) ?? [];

      const gradePoints: GradePoint[] = latestTranscript?.attributes.gradeConversion?.gradePoints ?? [];
      const gradePointsByCourseName = new Map<string, GradePoint>(
        gradePoints.map((gp) => [gp.educationCourseName, gp])
      );

      const tableRows: CourseRow[] = coursesData.map((course): CourseRow => {
        const conversion = course.courseName ? gradePointsByCourseName.get(course.courseName) : undefined;

        // Use confidence value from backend (fallback to FAILED if not provided)
        const confidence = course.confidence ?? 'FAILED';

        const institutionName = latestTranscript
          ? resolveAttributeValue(latestTranscript.attributes.schoolName) ?? item.schoolName
          : item.schoolName;

        return {
          courseName: course.courseName,
          courseCode: course.courseCode,
          marksObtained: course.marksObtained,
          grade: course.grade,
          credits: course.credits,
          semester: course.semester,
          arrow: 'mapped',
          mappedCourseName: conversion?.equivalentCourseName,
          mappedGrade: conversion?.convertedGradePoint,
          courseMappingId: conversion?.courseMappingId,
          // Metadata for creating new mappings
          educationCountryCode: item.country?.alpha2Code,
          educationLevel: item.level?.code,
          institutionName,
          confidence,
          manuallyEdited: course.manuallyEdited,
          aiGenerated: conversion?.aiGenerated,
          id: course.id, // Safe: filtered to ensure id exists
          transcriptDocumentId: latestTranscript?.id,
        };
      });

      const extractedOnRaw = latestTranscript?.attributes.extractedOn;
      const extractedOn = extractedOnRaw ? formatTranscriptDate(extractedOnRaw) : undefined;

      const updatedAtRaw = latestTranscript?.attributes.updatedAt;
      const updatedAt = formatTranscriptDate(updatedAtRaw);
      return {
        key: item.key,
        title: 'Courses and Grades',
        rows: tableRows,
        extractedOn,
        updatedAt,
        transcriptDocumentId: latestTranscript?.id,
        allCourses,
      };
    });
  }, [educationHistoryItems]);

  const applyConfidenceFilter = React.useCallback(
    (rows: CourseRow[], key: string): CourseRow[] => {
      const filter = confidenceFilterByKey[key] ?? 'ALL';
      if (filter === 'HIGH') return rows.filter((row) => row.confidence === 'HIGH');
      if (filter === 'MEDIUM') return rows.filter((row) => row.confidence === 'MEDIUM');
      if (filter === 'LOW') return rows.filter((row) => row.confidence === 'LOW');
      if (filter === 'FAILED') return rows.filter((row) => row.confidence === 'FAILED');
      if (filter === 'MANUAL') return rows.filter((row) => row.confidence === 'MANUAL');
      return rows;
    },
    [confidenceFilterByKey]
  );

  const sortRows = React.useCallback((rows: CourseRow[], sortState: TableColumnSort): CourseRow[] => {
    if (sortState.length === 0) return rows;
    const [{ id, desc }] = sortState;

    const getComparableValue = (row: CourseRow): number | string | null => {
      if (id === 'semester') {
        const value = row.semester;
        if (value == null) return null;
        const numeric = Number(value);
        return Number.isNaN(numeric) ? value : numeric;
      }

      if (id === 'marksObtained') {
        const value = row.marksObtained;
        if (value == null) return null;
        const numeric = Number(value);
        return Number.isNaN(numeric) ? value : numeric;
      }

      return null;
    };

    const compare = (a: CourseRow, b: CourseRow): number => {
      const aValue = getComparableValue(a);
      const bValue = getComparableValue(b);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      return String(aValue).localeCompare(String(bValue));
    };

    const sorted = [...rows].sort(compare);
    return desc ? sorted.reverse() : sorted;
  }, []);

  // Selection management helpers
  const getSelectedCoursesForKey = React.useCallback(
    (key: string): CourseRow[] => {
      return selectedCoursesByKey[key] || [];
    },
    [selectedCoursesByKey]
  );

  const toggleCourseSelection = React.useCallback((key: string, course: CourseRow) => {
    // All courses have IDs - no need for defensive check
    setSelectedCoursesByKey((prev) => {
      const currentSelection = prev[key] || [];
      const isAlreadySelected = currentSelection.some((c) => c.id === course.id);

      if (isAlreadySelected) {
        // Remove from selection
        return {
          ...prev,
          [key]: currentSelection.filter((c) => c.id !== course.id),
        };
      } else {
        // Add to selection
        return {
          ...prev,
          [key]: [...currentSelection, course],
        };
      }
    });
  }, []);

  const toggleSelectAllForKey = React.useCallback((key: string, visibleCourses: CourseRow[]) => {
    setSelectedCoursesByKey((prev) => {
      const currentSelection = prev[key] || [];
      const visibleCourseIds = visibleCourses.map((c) => c.id!); // All courses have IDs
      const allVisibleSelected = visibleCourseIds.every((id) => currentSelection.some((c) => c.id === id));

      if (allVisibleSelected) {
        // Deselect all visible courses
        const visibleIdSet = new Set(visibleCourseIds);
        return {
          ...prev,
          [key]: currentSelection.filter((c) => !visibleIdSet.has(c.id!)),
        };
      } else {
        // Select all visible courses (merge with existing selection)
        const existingIds = new Set(currentSelection.map((c) => c.id!));
        const newCourses = visibleCourses.filter((c) => !existingIds.has(c.id!));
        return {
          ...prev,
          [key]: [...currentSelection, ...newCourses],
        };
      }
    });
  }, []);

  const clearSelectionForKey = React.useCallback((key: string) => {
    setSelectedCoursesByKey((prev) => ({
      ...prev,
      [key]: [],
    }));
  }, []);

  const clearPendingCoursesForKey = React.useCallback((key: string) => {
    setPendingAddedCoursesByKey((prev) => ({
      ...prev,
      [key]: [],
    }));
  }, []);

  const getSelectAllState = React.useCallback(
    (key: string, visibleCourses: CourseRow[]): 'all' | 'some' | 'none' => {
      const selectedCourses = selectedCoursesByKey[key] || [];
      const visibleCourseIds = visibleCourses.map((c) => c.id!); // All courses have IDs

      if (visibleCourseIds.length === 0) return 'none';

      const selectedVisibleCount = visibleCourseIds.filter((id) => selectedCourses.some((c) => c.id === id)).length;

      if (selectedVisibleCount === 0) return 'none';
      if (selectedVisibleCount === visibleCourseIds.length) return 'all';
      return 'some';
    },
    [selectedCoursesByKey]
  );

  const isCourseSelected = React.useCallback(
    (key: string, courseId: string): boolean => {
      const selectedCourses = selectedCoursesByKey[key] || [];
      return selectedCourses.some((c) => c.id === courseId);
    },
    [selectedCoursesByKey]
  );

  // Handler for starting edit mode
  const handleEditClick = React.useCallback(
    (educationKey: string, course: CourseRow) => {
      const rowKey = `${educationKey}-${course.id}`;
      setEditingRowKey(rowKey);
      const initialValues = {
        courseName: course.courseName ?? '',
        courseCode: course.courseCode ?? '',
        marksObtained: course.marksObtained ?? '',
        grade: course.grade ?? '',
        credits: course.credits ?? '',
        semester: course.semester ?? '',
      };
      setEditedValues(initialValues);
      // Initialize refs with the same values
      courseNameRef.current = initialValues.courseName;
      courseCodeRef.current = initialValues.courseCode;
      marksObtainedRef.current = initialValues.marksObtained;
      gradeRef.current = initialValues.grade;
      creditsRef.current = initialValues.credits;
      semesterRef.current = initialValues.semester;
      setValidationErrors({});
      // Find the current mapped course ID from the courses array
      const currentCourse = courses.find((c) => c.name === course.mappedCourseName);
      setSelectedCourseId(currentCourse?.id ?? '');
      setHasDropdownChanged(false);
    },
    [courses, setSelectedCourseId, setEditingRowKey]
  );

  // Handler for confirming changes
  const handleConfirmClick = React.useCallback(
    async (educationKey: string, course: CourseRow, tableData: TableData) => {
      if (!editedValues) {
        setEditingRowKey(null);
        return;
      }

      const currentValues: EditingCourseState = {
        courseName: courseNameRef.current,
        courseCode: courseCodeRef.current,
        marksObtained: marksObtainedRef.current,
        grade: gradeRef.current,
        credits: creditsRef.current,
        semester: semesterRef.current,
      };

      const errors = validateCourse(currentValues);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      try {
        const hasFieldChanges =
          currentValues.courseName !== (course.courseName ?? '') ||
          currentValues.courseCode !== (course.courseCode ?? '') ||
          currentValues.marksObtained !== (course.marksObtained ?? '') ||
          currentValues.grade !== (course.grade ?? '') ||
          currentValues.credits !== (course.credits ?? '') ||
          currentValues.semester !== (course.semester ?? '');

        // Send PATCH request if any field changes OR if dropdown changes
        if ((hasFieldChanges || hasDropdownChanged) && course.transcriptDocumentId && studentId) {
          // Build the full courses array with the edited course
          const updatedCourses = tableData.allCourses.map((courseAttr) => {
            if (courseAttr.id === course.id) {
              return {
                id: courseAttr.id,
                schoolProvidedValue: {
                  courseName: currentValues.courseName ?? undefined,
                  courseCode: currentValues.courseCode ?? undefined,
                  marksObtained: currentValues.marksObtained ?? undefined,
                  grade: currentValues.grade ?? undefined,
                  credits: currentValues.credits ?? undefined,
                  semester: currentValues.semester ?? undefined,
                },
                manuallyEdited: true,
              };
            }
            return {
              id: courseAttr.id,
              schoolProvidedValue: courseAttr.schoolProvidedValue,
              manuallyEdited: courseAttr.manuallyEdited,
            };
          });

          await updateEducationHistoryCourses({
            studentId,
            educationHistoryId: course.transcriptDocumentId,
            courses: updatedCourses,
            applicationId,
          });
        }

        const hasCourseNameChange = currentValues.courseName !== (course.courseName ?? '');
        const hasBothChanges = hasCourseNameChange && hasDropdownChanged;
        let mappingResult: CourseMappingResult = null;

        if (hasCourseNameChange || hasDropdownChanged) {
          const equivalentCourseId = selectedCourseId || '';

          if (course.courseMappingId && !hasBothChanges) {
            // Single change with existing mapping → update
            mappingResult = await updateCourseMapping({
              courseMappingId: course.courseMappingId,
              sourceCourseName: currentValues.courseName || course.courseName || '',
              equivalentCourseId,
            });
          } else if (hasDropdownChanged) {
            // Both changes or dropdown-only without mapping → create
            mappingResult = await createCourseMapping({
              sourceCourseName: currentValues.courseName || course.courseName || '',
              equivalentCourseId,
              sourceCountryCode: course.educationCountryCode,
              sourceEducationLevel: course.educationLevel,
              sourceInstitutionName: course.institutionName,
            });
          }
          // Name change only does nothing
        }

        queryClient.setQueryData<Application>(['application', applicationId], (oldData) => {
          if (!oldData) return oldData;

          const newData = { ...oldData };
          const educationHistoriesData = { ...newData.educationHistories };
          const educationHistoryItemsData = [...educationHistoriesData.educationHistoryItems];

          const educationHistoryIndex = educationHistoryItemsData.findIndex((item) => item.key === educationKey);
          if (educationHistoryIndex === -1) return oldData;

          const educationHistory = { ...educationHistoryItemsData[educationHistoryIndex] };
          if (!educationHistory.transcriptExtractions) return oldData;

          const transcriptExtractions = [...educationHistory.transcriptExtractions];

          const latestTranscript = transcriptExtractions.sort(
            (a, b) =>
              new Date(b.attributes.extractedOn || 0).getTime() - new Date(a.attributes.extractedOn || 0).getTime()
          )[0];

          if (!latestTranscript) return oldData;

          const updatedTranscript = { ...latestTranscript };
          const attributes = { ...updatedTranscript.attributes };

          // Update courses if any changes were made
          if ((hasFieldChanges || hasDropdownChanged) && attributes.courses) {
            const existingCourses = attributes.courses;
            attributes.courses = existingCourses.map((c) => {
              if (c.id === course.id) {
                // Get the current course value (prioritize schoolProvidedValue, then extractedValue)
                const currentCourseValue = c.schoolProvidedValue ?? c.extractedValue ?? {};
                return {
                  ...c,
                  schoolProvidedValue: {
                    ...currentCourseValue,
                    courseName: currentValues.courseName ?? currentCourseValue.courseName,
                    courseCode: currentValues.courseCode ?? currentCourseValue.courseCode,
                    marksObtained: currentValues.marksObtained ?? currentCourseValue.marksObtained,
                    grade: currentValues.grade ?? currentCourseValue.grade,
                    credits: currentValues.credits ?? currentCourseValue.credits,
                    semester: currentValues.semester ?? currentCourseValue.semester,
                  },
                  manuallyEdited: true,
                };
              }
              return c;
            });
          }

          // Update grade conversion if mapping changed
          if (
            (hasCourseNameChange || hasDropdownChanged) &&
            course.courseMappingId &&
            !hasBothChanges &&
            mappingResult
          ) {
            const selectedCourseData = courses.find((c) => c.id === selectedCourseId);
            if (selectedCourseData) {
              const gradeConversion = attributes.gradeConversion
                ? { ...attributes.gradeConversion }
                : { gradePoints: [] };
              const gradePoints = [...(gradeConversion.gradePoints || [])];

              const courseNameToMatch = currentValues.courseName || course.courseName;
              const gradePointIndex = gradePoints.findIndex((gp) => gp.educationCourseName === courseNameToMatch);
              if (gradePointIndex !== -1) {
                gradePoints[gradePointIndex] = {
                  ...gradePoints[gradePointIndex],
                  equivalentCourseName: selectedCourseData.name,
                  equivalentCourseCode: selectedCourseData.id,
                  courseMappingId: mappingResult.id,
                };
              } else {
                gradePoints.push({
                  educationCourseName: courseNameToMatch ?? '',
                  equivalentCourseName: selectedCourseData.name,
                  equivalentCourseCode: selectedCourseData.id,
                  convertedGradePoint: '',
                  courseMappingId: mappingResult.id,
                });
              }

              gradeConversion.gradePoints = gradePoints;
              attributes.gradeConversion = gradeConversion;
            }
          }

          updatedTranscript.attributes = attributes;

          const transcriptIndex = transcriptExtractions.findIndex((t) => t.id === latestTranscript.id);
          if (transcriptIndex !== -1) {
            transcriptExtractions[transcriptIndex] = updatedTranscript;
          }

          educationHistory.transcriptExtractions = transcriptExtractions;
          educationHistoryItemsData[educationHistoryIndex] = educationHistory;
          educationHistoriesData.educationHistoryItems = educationHistoryItemsData;
          newData.educationHistories = educationHistoriesData;

          return newData;
        });

        queryClient.invalidateQueries(['application', applicationId]);

        triggerGpaRecalculation(educationKey);

        setEditingRowKey(null);
        setSelectedCourseId('');
        setHasDropdownChanged(false);
        setEditedValues(null);
        setValidationErrors({});
      } catch (error) {
        toast.negative(new Error('Failed to save changes. Please try again.'));
      }
    },
    [
      editedValues,
      selectedCourseId,
      hasDropdownChanged,
      applicationId,
      studentId,
      queryClient,
      updateCourseMapping,
      createCourseMapping,
      updateEducationHistoryCourses,
      courses,
      toast,
      triggerGpaRecalculation,
    ]
  );

  // Handler for canceling changes
  const handleCancelClick = React.useCallback(() => {
    setEditingRowKey(null);
    setSelectedCourseId('');
    setHasDropdownChanged(false);
    setEditedValues(null);
    setValidationErrors({});
  }, []);

  const handleOpenAddCourse = React.useCallback(
    (tableData: TableData) => {
      setAddCourseTable(tableData);
      setAddCourseValues(initialAddCourseState);
      setAddCourseErrors({});
      setIsAddCourseOpen(true);
    },
    [initialAddCourseState]
  );

  const handleCloseAddCourse = React.useCallback(() => {
    setIsAddCourseOpen(false);
    setAddCourseTable(null);
    setAddCourseErrors({});
    setAddCourseValues(initialAddCourseState);
  }, [initialAddCourseState]);

  const handleAddCourseChange = React.useCallback((field: keyof AddCourseState, value: string) => {
    setAddCourseValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleAddCourseSubmit = React.useCallback(async () => {
    if (!addCourseTable) return;

    const errors = validateNewCourse(addCourseValues);
    if (Object.keys(errors).length > 0) {
      setAddCourseErrors(errors);
      return;
    }

    if (!studentId || !addCourseTable.transcriptDocumentId) {
      toast.negative(new Error('Unable to add course. Missing student or transcript details.'));
      return;
    }

    const trimOrUndefined = (value: string) => {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    };

    const newCourseId = uuidv4();
    const newCoursePayload = {
      id: newCourseId,
      schoolProvidedValue: {
        courseCode: trimOrUndefined(addCourseValues.courseCode),
        courseName: trimOrUndefined(addCourseValues.courseName),
        marksObtained: trimOrUndefined(addCourseValues.marksObtained),
        grade: trimOrUndefined(addCourseValues.grade),
        credits: trimOrUndefined(addCourseValues.credits),
        semester: trimOrUndefined(addCourseValues.semester),
      },
    };

    const coursesPayload = [
      ...addCourseTable.allCourses.map((courseAttr) => ({
        id: courseAttr.id,
        schoolProvidedValue: courseAttr.schoolProvidedValue ?? undefined,
        manuallyEdited: courseAttr.manuallyEdited,
      })),
      newCoursePayload,
    ];

    // Temporarily add to local state for immediate feedback before API completes
    // Note: confidence will be determined by backend based on mapping status
    setPendingAddedCoursesByKey((prev) => {
      const current = prev[addCourseTable.key] ?? [];
      const newCourseRow: CourseRow = {
        courseName: trimOrUndefined(addCourseValues.courseName),
        courseCode: trimOrUndefined(addCourseValues.courseCode),
        marksObtained: trimOrUndefined(addCourseValues.marksObtained),
        grade: trimOrUndefined(addCourseValues.grade),
        credits: trimOrUndefined(addCourseValues.credits),
        semester: trimOrUndefined(addCourseValues.semester),
        confidence: undefined, // Backend will calculate based on mapping
        id: newCourseId,
        transcriptDocumentId: addCourseTable.transcriptDocumentId,
      };
      return { ...prev, [addCourseTable.key]: [...current, newCourseRow] };
    });

    try {
      await updateEducationHistoryCourses({
        studentId,
        educationHistoryId: addCourseTable.transcriptDocumentId,
        courses: coursesPayload,
        applicationId,
      });

      // Set loading state for refetch
      setIsWaitingForRefetch(true);

      // Wait for the application to be refetched before closing the dialog
      await queryClient.invalidateQueries(['application', applicationId]);

      triggerGpaRecalculation(addCourseTable.key);

      setIsAddCourseOpen(false);
      setAddCourseTable(null);
      setAddCourseErrors({});
      setAddCourseValues(initialAddCourseState);
      setIsWaitingForRefetch(false);
    } catch (error) {
      setIsWaitingForRefetch(false);
      setPendingAddedCoursesByKey((prev) => {
        if (!addCourseTable) {
          return prev;
        }
        const current = prev[addCourseTable.key] ?? [];
        const updated = current.filter((course) => course.id !== newCourseId);
        return { ...prev, [addCourseTable.key]: updated };
      });
      toast.negative(new Error('Failed to add course. Please try again.'));
    }
  }, [
    addCourseTable,
    addCourseValues,
    updateEducationHistoryCourses,
    studentId,
    queryClient,
    applicationId,
    toast,
    initialAddCourseState,
    triggerGpaRecalculation,
  ]);

  React.useEffect(() => {
    const updatedPages = { ...currentPageByKey };
    let changed = false;

    educationTables.forEach(({ key, rows }) => {
      const filtered = applyConfidenceFilter(rows, key);
      const sortState = sortStateByKey[key] ?? defaultSort;
      const sorted = sortRows(filtered, sortState);
      const maxPage = Math.max(1, Math.ceil(sorted.length / pageSize) || 1);
      const currentPage = currentPageByKey[key] ?? 1;
      if (currentPage > maxPage) {
        updatedPages[key] = maxPage;
        changed = true;
      }
    });

    if (changed) {
      setCurrentPageByKey(updatedPages);
    }
  }, [applyConfidenceFilter, currentPageByKey, educationTables, pageSize, sortRows, sortStateByKey, defaultSort]);

  return (
    <Flex direction="column" gap={4}>
      {educationTables.map(({ key, title, rows, extractedOn, updatedAt, transcriptDocumentId, allCourses }) => {
        // Filter out pending courses that already exist in real data (by ID)
        const existingIds = new Set(rows.map((row) => row.id).filter(Boolean));
        const pendingRows = (pendingAddedCoursesByKey[key] ?? []).filter((pending) => !existingIds.has(pending.id));
        const allRows = [...rows, ...pendingRows];
        const sortState = sortStateByKey[key] ?? defaultSort;
        const filterValue = confidenceFilterByKey[key] ?? 'ALL';
        const filteredRows = applyConfidenceFilter(allRows, key);
        const sortedRows = sortRows(filteredRows, sortState);
        const currentPage = currentPageByKey[key] ?? 1;
        const paginatedRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        const maxPage = Math.max(1, Math.ceil(sortedRows.length / pageSize) || 1);
        const highCount = rows.filter((row) => row.confidence === 'HIGH').length;
        const mediumCount = rows.filter((row) => row.confidence === 'MEDIUM').length;
        const lowCount = rows.filter((row) => row.confidence === 'LOW').length;
        const failedCount = rows.filter((row) => row.confidence === 'FAILED').length;
        const manualCount = rows.filter((row) => row.confidence === 'MANUAL').length;
        const isExpanded = expandedByKey[key] ?? true;
        const selectedCourses = getSelectedCoursesForKey(key);

        return (
          <Accordion
            key={key}
            value={isExpanded ? [key] : []}
            onChange={(val) => {
              const expanded = Array.isArray(val) ? val.includes(key) : val === key;
              setExpandedByKey((prev) => ({ ...prev, [key]: expanded }));
            }}
          >
            <Accordion.Item id={key} title={<Heading level={4}>{title}</Heading>}>
              <Flex justify="between" align="center" gap={4} pt={2} wrap>
                <Flex align="center" gap={4} wrap>
                  {extractedOn && (
                    <Text variant="bodyS" intent="secondary">
                      Extracted on: {extractedOn}
                    </Text>
                  )}
                  {mediumCount + lowCount + failedCount > 0 && (
                    <Tag size="sm" intent="warning">
                      {`${mediumCount + lowCount + failedCount} ${
                        mediumCount + lowCount + failedCount === 1 ? 'item needs' : 'items need'
                      } review`}
                    </Tag>
                  )}
                </Flex>
                <Flex align="center" gap={3} wrap>
                  <DeleteCoursesDialog
                    tableKey={key}
                    transcriptId={transcriptDocumentId ?? ''}
                    selectedCourses={selectedCourses}
                    allCourses={allCourses}
                    onClearSelection={clearSelectionForKey}
                    onClearPendingCourses={clearPendingCoursesForKey}
                    onRecalculate={triggerGpaRecalculation}
                    disabled={editingRowKey !== null || isLoadingCourses}
                  />
                  <div
                    style={{ width: 200 }}
                    role="presentation"
                    tabIndex={-1}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Select
                      value={filterValue}
                      aria-label={`Filter courses by confidence for ${title}`}
                      onChange={(value) => {
                        setConfidenceFilterByKey((prev) => ({ ...prev, [key]: value as ConfidenceFilter }));
                        setCurrentPageByKey((prev) => ({ ...prev, [key]: 1 }));
                        clearSelectionForKey(key); // Clear selection when filter changes
                      }}
                      appearance="styled"
                      emphasis="highlighted"
                      intent="positive"
                      disabled={allRows.length === 0}
                    >
                      <Select.Option label={`All Courses (${allRows.length})`} value="ALL" key="all-courses" />
                      <Select.Option label={`High (${highCount})`} value="HIGH" key="high-confidence" />
                      <Select.Option label={`Medium (${mediumCount})`} value="MEDIUM" key="medium-confidence" />
                      <Select.Option label={`Low (${lowCount})`} value="LOW" key="low-confidence" />
                      <Select.Option label={`Failed (${failedCount})`} value="FAILED" key="failed-confidence" />
                      <Select.Option label={`Manual (${manualCount})`} value="MANUAL" key="manual-confidence" />
                    </Select>
                  </div>
                  <Button
                    intent="primary"
                    emphasis="outlined"
                    size="sm"
                    disabled={editingRowKey !== null || isSaving}
                    onClick={() =>
                      handleOpenAddCourse({
                        key,
                        title,
                        rows,
                        extractedOn,
                        updatedAt,
                        transcriptDocumentId,
                        allCourses,
                      })
                    }
                  >
                    Add new courses
                  </Button>
                </Flex>
              </Flex>
              {allRows.length === 0 ? (
                <Flex pt={3}>
                  <Text variant="bodyM" intent="secondary">
                    No course data is available for this education history.
                  </Text>
                </Flex>
              ) : (
                <>
                  <StyledTable<CourseRow>
                    columnWidths={{
                      id: 64, // Checkbox column width
                    }}
                    data={paginatedRows}
                    loading={false}
                    overflow="scroll"
                    sort={sortState}
                    onSort={(columns) => {
                      if (columns.length === 0) {
                        setSortStateByKey((prev) => ({ ...prev, [key]: [] }));
                        return;
                      }
                      setSortStateByKey((prev) => ({ ...prev, [key]: columns }));
                      setCurrentPageByKey((prev) => ({ ...prev, [key]: 1 }));
                    }}
                    columns={[
                      // Checkbox column for course selection
                      {
                        accessorKey: 'id',
                        header: () => {
                          const visibleCourses = paginatedRows; // All courses have IDs
                          const selectAllState = getSelectAllState(key, visibleCourses);
                          const selectedCount = getSelectedCoursesForKey(key).length;

                          return (
                            <Flex justify="center" px={1}>
                              <Checkbox
                                value={selectAllState === 'all'}
                                indeterminate={selectAllState === 'some'}
                                onChange={() => toggleSelectAllForKey(key, visibleCourses)}
                                disabled={editingRowKey !== null || isSaving}
                                aria-label={`Select all courses on this page. ${selectedCount} course${
                                  selectedCount !== 1 ? 's' : ''
                                } currently selected.`}
                              />
                            </Flex>
                          );
                        },
                        cell: (course: CourseRow) => {
                          const isSelected = isCourseSelected(key, course.id!); // All courses have IDs

                          return (
                            <Table.Cell>
                              <Flex justify="center">
                                <Checkbox
                                  value={isSelected}
                                  onChange={() => toggleCourseSelection(key, course)}
                                  disabled={editingRowKey !== null || isSaving}
                                  aria-label={`Select ${course.courseName || 'course'} ${course.courseCode || ''}`}
                                />
                              </Flex>
                            </Table.Cell>
                          );
                        },
                        width: 'content',
                      },
                      {
                        accessorKey: 'courseName',
                        header: 'Course Name',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.courseName ?? ''}
                              valueRef={courseNameRef}
                              isEditing={isEditing}
                              displayValue={course.courseName || '-'}
                              ariaLabel="Course Name"
                              disabled={isSaving}
                              invalid={!!validationErrors.courseName}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'courseCode',
                        header: 'Course Code',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.courseCode ?? ''}
                              valueRef={courseCodeRef}
                              isEditing={isEditing}
                              displayValue={course.courseCode ?? '-'}
                              ariaLabel="Course Code"
                              disabled={isSaving}
                              invalid={!!validationErrors.courseCode}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'marksObtained',
                        enableSorting: true,
                        header: 'Marks Obtained',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.marksObtained ?? ''}
                              valueRef={marksObtainedRef}
                              isEditing={isEditing}
                              displayValue={course.marksObtained || '-'}
                              ariaLabel="Marks Obtained"
                              disabled={isSaving}
                              invalid={!!validationErrors.marksObtained}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'grade',
                        header: 'Grade',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.grade ?? ''}
                              valueRef={gradeRef}
                              isEditing={isEditing}
                              displayValue={course.grade ?? '-'}
                              ariaLabel="Grade"
                              disabled={isSaving}
                              invalid={!!validationErrors.grade}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'credits',
                        header: 'Credits',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.credits ?? ''}
                              valueRef={creditsRef}
                              isEditing={isEditing}
                              displayValue={course.credits || '-'}
                              ariaLabel="Credits"
                              disabled={isSaving}
                              invalid={!!validationErrors.credits}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'semester',
                        enableSorting: true,
                        header: 'Semester',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey && editedValues !== null;

                          return (
                            <EditableTextCell
                              value={editedValues?.semester ?? ''}
                              valueRef={semesterRef}
                              isEditing={isEditing}
                              displayValue={course.semester || '-'}
                              ariaLabel="Semester"
                              disabled={isSaving}
                              invalid={!!validationErrors.semester}
                            />
                          );
                        },
                      },
                      {
                        accessorKey: 'arrow',
                        header: '',
                        cell: () => (
                          <Table.Cell>
                            <ArrowRightOutlineIcon size="md" />
                          </Table.Cell>
                        ),
                      },
                      {
                        accessorKey: 'mappedCourseName',
                        header: 'Mapped to course name',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey;

                          if (isEditing) {
                            return (
                              <Table.Cell>
                                <Select
                                  value={selectedCourseId}
                                  onChange={(value) => {
                                    setSelectedCourseId(value);
                                    setHasDropdownChanged(true);
                                  }}
                                  placeholder="Select a course"
                                  aria-label="Select mapped course"
                                  appearance="styled"
                                  emphasis="highlighted"
                                  disabled={isLoadingCourses || isSaving}
                                >
                                  {courses.map((c) => (
                                    <Select.Option key={c.id} value={c.id} label={c.name} />
                                  ))}
                                </Select>
                              </Table.Cell>
                            );
                          }

                          const showAiSuggested = course.aiGenerated === true && !course.manuallyEdited;
                          return (
                            <Table.Cell>
                              <Flex direction="column" gap={1}>
                                <Text>{course.mappedCourseName ?? '-'}</Text>
                                {showAiSuggested && (
                                  <Text variant="bodyS" intent="negative">
                                    AI Suggested
                                  </Text>
                                )}
                              </Flex>
                            </Table.Cell>
                          );
                        },
                      },
                      {
                        accessorKey: 'mappedGrade',
                        header: 'Mapped Grade',
                        cell: (course: CourseRow) => <Table.TextCell>{course.mappedGrade ?? '-'}</Table.TextCell>,
                      },
                      {
                        accessorKey: 'confidence',
                        header: 'Confidence',
                        cell: (course: CourseRow) => {
                          const getConfidenceTag = (confidence?: string) => {
                            if (!confidence) return <Table.TextCell>-</Table.TextCell>;

                            const configs = {
                              HIGH: { intent: 'positive' as const, label: 'High' },
                              MEDIUM: { intent: 'warning' as const, label: 'Medium' },
                              LOW: { intent: 'negative' as const, label: 'Low' },
                              FAILED: { intent: 'negative' as const, label: 'Failed' },
                              MANUAL: { intent: 'secondary' as const, label: 'Manual' },
                            };

                            const config = configs[confidence as keyof typeof configs];
                            if (!config) return <Table.TextCell>-</Table.TextCell>;

                            return (
                              <Tag size="sm" intent={config.intent}>
                                {config.label}
                              </Tag>
                            );
                          };

                          return <Table.Cell>{getConfidenceTag(course.confidence)}</Table.Cell>;
                        },
                      },
                      {
                        accessorKey: 'actions',
                        header: 'Actions',
                        cell: (course: CourseRow) => {
                          const rowKey = `${key}-${course.id}`;
                          const isEditing = editingRowKey === rowKey;

                          if (isEditing) {
                            return (
                              <Table.Cell>
                                <Flex gap={1}>
                                  <Button
                                    aria-label="Confirm changes"
                                    leadIcon={CheckMarkOutlineIcon}
                                    size="sm"
                                    intent="positive"
                                    emphasis="transparent"
                                    onClick={() =>
                                      handleConfirmClick(key, course, {
                                        key,
                                        title,
                                        rows,
                                        extractedOn,
                                        transcriptDocumentId,
                                        allCourses,
                                        updatedAt,
                                      })
                                    }
                                    disabled={isSaving}
                                    loading={isSaving}
                                  />
                                  <Button
                                    aria-label="Cancel changes"
                                    leadIcon={CloseOutlineIcon}
                                    size="sm"
                                    intent="secondary"
                                    emphasis="transparent"
                                    onClick={handleCancelClick}
                                    disabled={isSaving}
                                  />
                                </Flex>
                              </Table.Cell>
                            );
                          }

                          const showFixButton = course.confidence === 'LOW' || course.confidence === 'FAILED';

                          return (
                            <Table.Cell>
                              <Button
                                aria-label={showFixButton ? 'Fix course data' : 'Edit course mapping'}
                                leadIcon={showFixButton ? undefined : EditOutlineIcon}
                                size="sm"
                                intent={showFixButton ? 'positive' : 'secondary'}
                                emphasis={showFixButton ? 'highlighted' : 'transparent'}
                                onClick={() => handleEditClick(key, course)}
                                disabled={editingRowKey !== null || isLoadingCourses}
                              >
                                {showFixButton ? 'Fix' : undefined}
                              </Button>
                            </Table.Cell>
                          );
                        },
                      },
                    ]}
                  />
                  <Flex direction="column" align="center" gap={3} pt={4}>
                    <Flex align="center" gap={3} wrap>
                      <Button
                        aria-label="Previous page"
                        intent="secondary"
                        emphasis="highlighted"
                        disabled={currentPage <= 1 || sortedRows.length === 0}
                        leadIcon={ChevronLeftOutlineIcon}
                        onClick={() => {
                          setCurrentPageByKey((prev) => ({ ...prev, [key]: Math.max(1, currentPage - 1) }));
                          // Selection preserved across pages for cross-page deletion
                        }}
                      >
                        {/* icon-only button */}{' '}
                      </Button>
                      <Flex align="center" gap={1} wrap>
                        {Array.from({ length: maxPage }, (_, idx) => idx + 1).map((pageNumber) => {
                          const isSelected = pageNumber === currentPage;
                          return (
                            <Button
                              key={pageNumber}
                              aria-current={isSelected ? 'true' : 'false'}
                              aria-label={`Go to page ${pageNumber}`}
                              intent={isSelected ? 'positive' : 'secondary'}
                              emphasis={isSelected ? 'highlighted' : 'transparent'}
                              size="sm"
                              disabled={sortedRows.length === 0}
                              onClick={() => {
                                setCurrentPageByKey((prev) => ({ ...prev, [key]: pageNumber }));
                                // Selection preserved across pages for cross-page deletion
                              }}
                            >
                              {pageNumber.toString()}
                            </Button>
                          );
                        })}
                      </Flex>
                      <Button
                        aria-label="Next page"
                        intent="secondary"
                        emphasis="highlighted"
                        disabled={currentPage >= maxPage || sortedRows.length === 0}
                        leadIcon={ChevronRightOutlineIcon}
                        onClick={() => {
                          setCurrentPageByKey((prev) => ({ ...prev, [key]: Math.min(maxPage, currentPage + 1) }));
                          // Selection preserved across pages for cross-page deletion
                        }}
                      >
                        {/* icon-only button */}{' '}
                      </Button>
                    </Flex>
                  </Flex>
                </>
              )}
            </Accordion.Item>
          </Accordion>
        );
      })}
      <AddCourseDialog
        open={isAddCourseOpen}
        isSaving={isSaving}
        values={addCourseValues}
        errors={addCourseErrors}
        onChange={handleAddCourseChange}
        onClose={handleCloseAddCourse}
        onSubmit={handleAddCourseSubmit}
      />
    </Flex>
  );
}
